/* eslint-disable @typescript-eslint/consistent-type-definitions -- Zero mutator/query arg types must be `type` aliases to satisfy the ReadonlyJSONValue constraint; interfaces lack an implicit index signature. */
import { articleIconOrDefault } from "@productlane/lib/src/nucleo-icon-search"
import {
	decideCreateOutcome,
	decideDeleteOutcome,
	decideEditOutcome,
	stepsForRetainedHunks,
} from "@productlane/lib/src/prosemirror-diff"

import type { HelpCenterArticleVersion, MutatorTx } from "../schema"
import type { JSONValue } from "@rocicorp/zero"
import { loadVersionChain } from "./help-center-version-chain"
import { pruneOldVersions } from "./help-center-version-prune"

// Per-kind apply helpers shared by `proposals.accept` and
// `proposalGroups.acceptGroup`. Each helper is a thin imperative shell: it
// gathers the inputs the corresponding pure decider needs, calls the decider
// to determine `apply` vs `supersede`, and then executes the writes.
//
// All branching/decision logic lives in `proposal-decisions.ts` (lib) and is
// fully unit-tested there. The shells in this file own only I/O — querying
// rows, inserting/updating tables, mirroring translations.
//
// Result shape is a discriminated union so callers map directly to a proposal
// row update without re-deriving status:
//   - `accepted`  — article-side mutations succeeded.
//   - `superseded` — preconditions failed; caller flips status without writing
//     a reason (the column was removed for simplicity).

export type ApplyOutcome = { kind: "accepted" } | { kind: "superseded" }

export type ApplyContext = {
	workspaceId: string
	now: number
}

type ProposalLike = {
	id: string
	articleId?: string | null
	baseVersionId?: string | null
	proposedSteps: unknown
	proposedTitle?: string | null
	proposedGroupId?: string | null
	proposedIcon?: string | null
}

function stepsOf(proposal: ProposalLike): unknown[] {
	return Array.isArray(proposal.proposedSteps)
		? (proposal.proposedSteps as unknown[])
		: []
}

function superseded(): ApplyOutcome {
	return { kind: "superseded" }
}

export async function applyDeleteProposal(
	tx: MutatorTx,
	proposal: ProposalLike,
	ctx: ApplyContext,
): Promise<ApplyOutcome> {
	const articleId = proposal.articleId ?? null
	const article = articleId
		? ((
				await tx.query.helpCenterArticles.where("id", "=", articleId).run()
			)[0] ?? null)
		: null

	const decision = decideDeleteOutcome({
		articleId,
		article: article ? { isDeleted: article.isDeleted } : null,
	})
	if (decision.kind === "supersede") return superseded()

	await tx.mutate.helpCenterArticles.update({
		// `decideDeleteOutcome` guarantees article is non-null on `delete`.
		id: article!.id,
		isDeleted: true,
		updatedAt: ctx.now,
	})
	return { kind: "accepted" }
}

export type CreateProposalIds = {
	newArticleId: string
	newArticleUrlName: string
	editedHtml?: string
	editedTitle?: string
}

export async function applyCreateProposal(
	tx: MutatorTx,
	proposal: ProposalLike,
	ids: CreateProposalIds,
	ctx: ApplyContext,
): Promise<ApplyOutcome> {
	const proposedGroupId = proposal.proposedGroupId ?? null

	let groupExists: boolean | null = null
	let groupPortalInstanceId: string | null = null
	if (proposedGroupId) {
		const [existingGroup] = await tx.query.helpCenterGroups
			.where("id", "=", proposedGroupId)
			.run()
		groupExists = !!existingGroup
		groupPortalInstanceId = existingGroup?.portalInstanceId ?? null
	}

	const decision = decideCreateOutcome({
		proposedTitle: proposal.proposedTitle ?? null,
		proposedGroupId,
		groupExists,
		stepsJson: stepsOf(proposal),
	})
	if (decision.kind === "supersede") return superseded()

	let order = 0
	if (proposedGroupId) {
		const siblings = await tx.query.helpCenterArticles
			.where("helpCenterGroupId", "=", proposedGroupId)
			.where("isDeleted", "=", false)
			.run()
		order =
			siblings.length > 0 ? Math.max(...siblings.map((a) => a.order)) + 1 : 0
	}

	await tx.mutate.helpCenterArticles.insert({
		id: ids.newArticleId,
		title: ids.editedTitle?.trim() || decision.title,
		helpCenterGroupId: proposedGroupId ?? undefined,
		portalInstanceId: groupPortalInstanceId ?? undefined,
		order,
		createdAt: ctx.now,
		updatedAt: ctx.now,
		isDeleted: false,
		version: 1,
		workspaceId: ctx.workspaceId,
		archived: false,
		kind: "DOC",
		externalUrl: null,
		content: ids.editedHtml ?? decision.html,
		summary: "",
		// An accepted suggestion is a draft; a person publishes it after reading it in place.
		published: false,
		visibility: "PUBLIC",
		icon: articleIconOrDefault(proposal.proposedIcon),
		urlName: ids.newArticleUrlName,
		showOnHomePage: true,
	})
	return { kind: "accepted" }
}

export type EditProposalIds = {
	newArticleVersionId: string
	discardedHunkIds?: string[]
	editedHtml?: string
	editedTitle?: string
}

// The editor and the portal read the default language's own translation row,
// so an accepted edit has to land there too, not only on the article.
async function mirrorDefaultLanguageTranslation(
	tx: MutatorTx,
	args: {
		workspaceId: string
		articleId: string
		html: string
		title: string
	},
): Promise<void> {
	const [workspace] = await tx.query.workspaces
		.where("id", "=", args.workspaceId)
		.run()
	const [existing] = await tx.query.helpCenterArticleTranslations
		.where("helpCenterArticleId", "=", args.articleId)
		.where("language", "=", workspace?.defaultLanguage ?? "en")
		.run()
	if (!existing) return
	await tx.mutate.helpCenterArticleTranslations.update({
		id: existing.id,
		title: args.title,
		content: args.html,
	})
}

async function applyProposalTranslations(
	tx: MutatorTx,
	args: { proposalId: string; articleId: string },
): Promise<void> {
	const drafts = await tx.query.helpCenterArticleProposalTranslations
		.where("proposalId", "=", args.proposalId)
		.run()

	for (const draft of drafts) {
		if (draft.translationStatus !== "COMPLETED") continue
		if (!draft.content) continue
		const [existing] = await tx.query.helpCenterArticleTranslations
			.where("helpCenterArticleId", "=", args.articleId)
			.where("language", "=", draft.language)
			.run()
		if (!existing) continue
		await tx.mutate.helpCenterArticleTranslations.update({
			id: existing.id,
			title: draft.title ?? existing.title,
			content: draft.content,
		})
	}
}

export async function applyEditProposal(
	tx: MutatorTx,
	proposal: ProposalLike,
	ids: EditProposalIds,
	ctx: ApplyContext,
): Promise<ApplyOutcome> {
	if (!proposal.articleId) {
		return superseded()
	}
	const [article] = await tx.query.helpCenterArticles
		.where("id", "=", proposal.articleId)
		.run()

	const reviewerEdited =
		ids.editedHtml !== undefined || ids.editedTitle !== undefined
	if (article && reviewerEdited) {
		const reviewedTitle =
			ids.editedTitle?.trim() || proposal.proposedTitle?.trim() || article.title
		const reviewedHtml =
			ids.editedHtml ??
			(typeof article.content === "string" ? article.content : "")
		await tx.mutate.helpCenterArticles.update({
			id: article.id,
			content: reviewedHtml,
			title: reviewedTitle,
			version: article.version + 1,
			updatedAt: ctx.now,
		})
		await mirrorDefaultLanguageTranslation(tx, {
			workspaceId: ctx.workspaceId,
			articleId: article.id,
			html: reviewedHtml,
			title: reviewedTitle,
		})
		await applyProposalTranslations(tx, {
			proposalId: proposal.id,
			articleId: article.id,
		})
		return { kind: "accepted" }
	}

	const chain = article
		? await loadVersionChain(tx, article.id, proposal.baseVersionId ?? null)
		: { baseHtml: null, currentHtml: "", chainSteps: null }
	// Drop discarded hunks against the proposal's draft base (the same content
	// `proposedSteps` were authored on) so the recomputed ids match the client's.
	const originalSteps = stepsOf(proposal)
	let stepsJson = originalSteps
	if (ids.discardedHunkIds?.length) {
		stepsJson =
			chain.baseHtml != null
				? stepsForRetainedHunks(
						proposal.id,
						chain.baseHtml,
						originalSteps,
						new Set(ids.discardedHunkIds),
					)
				: originalSteps
		// Fail safe: if the skip couldn't be honored (null base, or the reduction
		// fell back to the full set), supersede rather than silently applying the
		// changes the reviewer explicitly skipped.
		if (stepsJson === originalSteps) {
			console.warn(
				`applyEditProposal: could not honor ${ids.discardedHunkIds.length} skipped hunk(s) for proposal ${proposal.id}; superseding`,
			)
			return superseded()
		}
	}

	const decision = decideEditOutcome({
		article: article
			? { isDeleted: article.isDeleted, title: article.title }
			: null,
		chain,
		stepsJson,
		proposedTitle: proposal.proposedTitle ?? null,
	})
	if (decision.kind === "supersede") return superseded()

	// `decideEditOutcome` guarantees `article` is non-null on `apply`.
	const articleRow = article!
	await tx.mutate.helpCenterArticles.update({
		id: articleRow.id,
		content: ids.editedHtml ?? decision.appliedHtml,
		title: ids.editedTitle?.trim() || decision.newTitle,
		version: articleRow.version + 1,
		updatedAt: ctx.now,
	})

	await mirrorDefaultLanguageTranslation(tx, {
		workspaceId: ctx.workspaceId,
		articleId: articleRow.id,
		html: decision.appliedHtml,
		title: decision.newTitle,
	})

	const previousHead = await pruneOldVersions(tx, articleRow.id)

	await applyProposalTranslations(tx, {
		proposalId: proposal.id,
		articleId: articleRow.id,
	})

	await tx.mutate.helpCenterArticleVersions.insert({
		id: ids.newArticleVersionId,
		articleId: articleRow.id,
		createdAt: ctx.now,
		version: articleRow.version + 1,
		title: decision.newTitle,
		content: decision.appliedHtml,
		imageUrl: articleRow.imageUrl ?? null,
		imageBlurhash: articleRow.imageBlurhash ?? null,
		summary: articleRow.summary ?? "",
		icon: articleRow.icon,
		language: "en",
		appliedSteps: decision.rebasedSteps as JSONValue,
		parentVersionId: previousHead?.id ?? null,
		createdBySource: "AI",
		createdById: null,
	})

	return { kind: "accepted" }
}

// Re-export so `pruneOldVersions` keeps its old import path for any external
// caller (none today, but the helper is fundamentally about version pruning,
// not proposal-apply, and now lives in its own file for testability).
export type { HelpCenterArticleVersion }
